<template>
  <div class="gr-filters">
    <div class="gr-container gr-filters__inner">
      <div class="gr-filters__row">
        <div class="gr-filters__search">
          <v-text-field
            v-model="searchQuery"
            variant="outlined"
            density="comfortable"
            hide-details
            single-line
            clearable
            append-inner-icon="mdi-magnify"
            placeholder="Cari aktivitas . . ."
            aria-label="Cari aktivitas"
            @update:model-value="debouncedUpdateFilter"
          />
        </div>

        <div class="gr-filters__controls">
          <!-- One sort control at every width. Canonical shows a menu on
               desktop and a tab strip on mobile, both behind ClientOnly
               because they depend on viewport detection; a single select needs
               no viewport probe and so survives server rendering. -->
          <v-select
            v-model="selectedSort"
            :items="sorts"
            item-title="title"
            item-value="value"
            variant="outlined"
            density="comfortable"
            hide-details
            label="Urutkan"
            :menu-props="{ class: 'gr-overlay' }"
            class="gr-filters__sort"
            @update:model-value="updateFilter"
          />

          <button
            type="button"
            class="gr-btn gr-btn--outline gr-filters__filter-btn"
            @click="openLocationDialog"
          >
            <v-icon icon="mdi-filter-variant" size="18" aria-hidden="true" />
            Filter
            <span v-if="isFilterApplied" class="gr-filters__dot">
              <span class="gr-filters__dot-mark" aria-hidden="true" />
              <span class="gr-filters__sr">lokasi aktif</span>
            </span>
          </button>
        </div>
      </div>

      <div
        class="gr-filters__status"
        role="group"
        aria-label="Filter status aktivitas"
      >
        <button
          v-for="option in statusOptions"
          :key="option.value ?? 'all'"
          type="button"
          class="gr-filters__chip"
          :class="{
            'gr-filters__chip--active': option.value === selectedStatus,
          }"
          :aria-current="option.value === selectedStatus ? 'true' : undefined"
          @click="handleStatusChange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <ActivitiesLocationDialog
      :open="isShowDialogFilter"
      :filter="locationFilter"
      @close="closeLocationDialog"
      @apply="handleApplyFilter"
    />
  </div>
</template>

<script lang="ts" setup>
import { useDebounceFn } from "@vueuse/core";
import ActivitiesLocationDialog from "@/features/Activity/views/components/ActivitiesLocationDialog.vue";
import {
  ACTIVITY_STATUS_LABEL,
  type ActivityStatus,
} from "@/common/utils/activityStatus";

type SortValue =
  | "latest"
  | "oldest"
  | "nearest_reg"
  | "oldest_reg"
  | "finished_recent";

interface FilterState {
  query?: string;
  provinceId?: number;
  cityId?: number;
  sort?: SortValue;
  status?: ActivityStatus;
}

interface LocationFilterState {
  province?: number;
  city?: number;
}

const props = defineProps<{
  initialFilter?: FilterState;
  isFilterApplied?: boolean;
}>();

const emit = defineEmits<{ "update:filter": [filter: FilterState] }>();

// "Semua" carries no value, so the status param is omitted entirely.
const statusOptions: { label: string; value?: ActivityStatus }[] = [
  { label: "Semua", value: undefined },
  ...(Object.keys(ACTIVITY_STATUS_LABEL) as ActivityStatus[]).map((value) => ({
    label: ACTIVITY_STATUS_LABEL[value],
    value,
  })),
];

const ALL_SORTS: { title: string; value: SortValue }[] = [
  { title: "Terbaru", value: "latest" },
  { title: "Terlama", value: "oldest" },
  { title: "Registrasi terdekat", value: "nearest_reg" },
  { title: "Registrasi terjauh", value: "oldest_reg" },
  { title: "Terakhir selesai", value: "finished_recent" },
];

const selectedStatus = ref<ActivityStatus | undefined>(
  props.initialFilter?.status,
);
const searchQuery = ref(props.initialFilter?.query || "");
const selectedProvince = ref<number | undefined>(
  props.initialFilter?.provinceId,
);
const selectedCity = ref<number | undefined>(props.initialFilter?.cityId);
const selectedSort = ref<SortValue>(props.initialFilter?.sort ?? "latest");
const isShowDialogFilter = ref(false);

// The dialog takes a one-way `:open` prop plus a `@close` event rather than
// `v-model`, so Vuetify never sees the binding it uses to restore focus and
// drops it on `<body>` when the dialog closes. A keyboard user who pressed
// Escape would then have to tab from the top of the document to get back to
// the Filter button (WCAG 2.4.3). Hold the trigger ourselves.
let dialogTrigger: HTMLElement | null = null;

function openLocationDialog(): void {
  dialogTrigger =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  isShowDialogFilter.value = true;
}

function closeLocationDialog(): void {
  isShowDialogFilter.value = false;
  const trigger = dialogTrigger;
  dialogTrigger = null;
  if (trigger) void nextTick(() => trigger.focus({ preventScroll: true }));
}

const isFinishedMode = computed(() => selectedStatus.value === "FINISHED");

// Finished mode offers end-date order and drops registration-based order.
const sorts = computed(() =>
  ALL_SORTS.filter((sort) =>
    isFinishedMode.value
      ? sort.value !== "nearest_reg" && sort.value !== "oldest_reg"
      : sort.value !== "finished_recent",
  ),
);

const locationFilter = ref<LocationFilterState>({
  province: selectedProvince.value,
  city: selectedCity.value,
});

function updateFilter(): void {
  emit("update:filter", {
    query: searchQuery.value || undefined,
    provinceId: selectedProvince.value,
    cityId: selectedCity.value,
    sort: selectedSort.value,
    status: selectedStatus.value,
  });
}

const debouncedUpdateFilter = useDebounceFn(updateFilter, 300);

// A mode switch can drop the active sort from the list. Fall back to the first
// remaining option and write it back, so the control never drifts out of sync
// with the URL.
watch(sorts, () => {
  if (!sorts.value.some((sort) => sort.value === selectedSort.value)) {
    selectedSort.value = sorts.value[0].value;
    updateFilter();
  }
});

function handleStatusChange(value?: ActivityStatus): void {
  if (value === selectedStatus.value) return;
  selectedStatus.value = value;
  updateFilter();
}

function handleApplyFilter(updated: LocationFilterState): void {
  locationFilter.value = updated;
  selectedProvince.value = updated.province;
  selectedCity.value = updated.city;
  updateFilter();
}

watch(
  () => props.initialFilter,
  (next) => {
    if (!next) return;
    searchQuery.value = next.query || "";
    selectedProvince.value = next.provinceId;
    selectedCity.value = next.cityId;
    selectedStatus.value = next.status;
    locationFilter.value = { province: next.provinceId, city: next.cityId };
    if (next.sort) selectedSort.value = next.sort;
  },
  { deep: true },
);
</script>

<style scoped>
.gr-filters {
  position: sticky;
  /* Clears the 72px candidate masthead plus its hairline. */
  top: 73px;
  z-index: 5;
  border-bottom: 1px solid var(--gr-line);
  background-color: rgba(250, 249, 245, 0.96);
  backdrop-filter: saturate(1.4) blur(6px);
}

.gr-filters__inner {
  padding-block: 14px;
}

.gr-filters__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.gr-filters__search {
  flex: 1 1 260px;
  min-width: 0;
}

.gr-filters__controls {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 8px;
}

.gr-filters__sort {
  min-width: 200px;
}

.gr-filters__filter-btn {
  position: relative;
  flex: 0 0 auto;
}

.gr-filters__dot {
  display: inline-flex;
  align-items: center;
}

.gr-filters__dot-mark {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background-color: var(--gr-green-deep);
}

.gr-filters__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.gr-filters__status {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.gr-filters__chip {
  min-height: 36px;
  padding: 6px 14px;
  border: 1px solid var(--gr-line-strong);
  border-radius: 9999px;
  background-color: transparent;
  color: var(--gr-ink-2);
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background-color 120ms cubic-bezier(0.2, 0, 0, 1),
    border-color 120ms cubic-bezier(0.2, 0, 0, 1),
    color 120ms cubic-bezier(0.2, 0, 0, 1);
}

/* Active state differs by fill, not colour alone. */
.gr-filters__chip--active {
  border-color: var(--gr-green-deep);
  background-color: var(--gr-green-deep);
  color: #ffffff;
}

@media (hover: hover) {
  /* :not() keeps this off the active chip. Without it the rule outranks
     `.gr-filters__chip--active` — three classes against two — and paints
     the label the same green as its own fill, so the active chip's text
     disappears under the pointer. */
  .gr-filters__chip:not(.gr-filters__chip--active):hover {
    border-color: var(--gr-green-deep);
    color: var(--gr-green-deep);
  }

  /* The active chip darkens its fill instead and keeps white text. */
  .gr-filters__chip--active:hover {
    border-color: #164a19;
    background-color: #164a19;
    color: #ffffff;
  }
}

@media (max-width: 560px) {
  .gr-filters__controls {
    flex: 1 1 100%;
  }

  .gr-filters__sort {
    flex: 1 1 auto;
    min-width: 0;
  }

  .gr-filters__status {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
    scrollbar-width: thin;
  }

  .gr-filters__chip {
    flex: 0 0 auto;
  }
}
</style>
